import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { locField } from "@/lib/i18nField";
import { formatDate } from "@/lib/format";
import TranslateText from "@/components/community/TranslateText";
import ReactionBar from "@/components/community/ReactionBar";
import ReportDialog from "@/components/community/ReportDialog";
import BlockButton from "@/components/community/BlockButton";
import AuthorBadges from "@/components/community/AuthorBadges";
import { ArrowLeft, Send, User, ShieldCheck, Bell, BellOff, Share2, Trash2, Link2 } from "lucide-react";

export default function CommunityPost() {
  const { id } = useParams();
  const { lang, t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [following, setFollowing] = useState(false);

  const { data, isLoading } = useQuery({ queryKey: ["comPost", id], queryFn: async () => (await api.get(`/community/posts/${id}`)).data });
  React.useEffect(() => {
    if (!user) return;
    api.get("/community/follows").then((r) => setFollowing(r.data.some((f) => f.target_type === "post" && f.target_id === id && f.mode !== "none"))).catch(() => {});
  }, [user, id]);

  if (isLoading) return <div className="py-24 text-center text-stone-400">{t("loading")}</div>;
  if (!data?.post) return <div className="py-24 text-center text-stone-400">404</div>;
  const post = data.post;
  const title = locField(post.title, lang);
  const content = locField(post.content, lang);
  const official = post.is_official || post.author_role === "official";
  const topComments = (data.comments || []).filter((c) => !c.parent_id);
  const repliesOf = (cid) => (data.comments || []).filter((c) => c.parent_id === cid);

  const send = async () => {
    if (!user) { toast.error(t("com_login_required")); return; }
    if (!body.trim()) return;
    try {
      await api.post(`/community/posts/${id}/comments`, { content: body, parent_id: replyTo });
      setBody(""); setReplyTo(null);
      qc.invalidateQueries({ queryKey: ["comPost", id] });
    } catch (e) { toast.error(e?.response?.data?.detail || t("com_error")); }
  };
  const toggleFollow = async () => {
    if (!user) { toast.error(t("com_login_required")); return; }
    try { await api.post("/community/follow", { target_type: "post", target_id: id, mode: following ? "none" : "all" }); setFollowing(!following); } catch { toast.error(t("com_error")); }
  };
  const share = () => { navigator.clipboard?.writeText(window.location.href); toast.success(t("com_link_copied")); };
  const del = async () => { try { await api.delete(`/community/posts/${id}`); toast.success(t("com_saved")); navigate("/community"); } catch { toast.error(t("com_error")); } };
  const delComment = async (cid) => { try { await api.delete(`/community/comments/${cid}`); qc.invalidateQueries({ queryKey: ["comPost", id] }); } catch { toast.error(t("com_error")); } };

  const Comment = ({ c, isReply }) => (
    <div data-testid={`comment-${c.id}`} className={`flex gap-3 ${isReply ? "ml-8 sm:ml-12" : ""}`}>
      {c.author_picture ? <img src={c.author_picture} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" /> : <span className="w-8 h-8 rounded-full bg-amber-900/40 flex items-center justify-center shrink-0"><User className="w-4 h-4 text-amber-400" /></span>}
      <div className="flex-1 min-w-0 bg-[#121418] border border-[#272A34] rounded-lg p-3">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="font-serif text-sm text-stone-100 flex items-center gap-1">{c.author_name}{c.author_role === "official" && <ShieldCheck className="w-3.5 h-3.5 text-amber-500" />}</span>
          <span className="flex items-center gap-1.5"><AuthorBadges badges={c.author_badges} /><span className="font-mono text-[10px] text-stone-500">{formatDate(c.created_at, lang)}</span></span>
        </div>
        <TranslateText text={c.content} originalLanguage={c.original_language} />
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          <ReactionBar targetType="comment" targetId={c.id} counts={c.reaction_counts} mine={c.my_reactions} />
          {!isReply && user && <button data-testid={`reply-btn-${c.id}`} onClick={() => setReplyTo(c.id)} className="text-xs text-amber-500 hover:text-amber-400">{t("com_reply")}</button>}
          {user && (c.author_id === user.user_id || user.role === "admin") && <button data-testid={`del-comment-${c.id}`} onClick={() => delComment(c.id)} className="text-xs text-stone-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>}
          {user && c.author_id !== user.user_id && <ReportDialog targetType="comment" targetId={c.id} />}
          {user && !isReply && c.author_id !== user.user_id && <BlockButton userId={c.author_id} />}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Seo title={title || t("com_title")} description={(content || "").slice(0, 160)} canonicalPath={`/community/post/${id}`} noindex={post.visibility === "members"} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-10">
        <Link to="/community" className="inline-flex items-center gap-1.5 text-stone-300 hover:text-amber-400 text-sm mb-5"><ArrowLeft className="w-4 h-4" /> {t("com_title")}</Link>

        <article className="bg-[#121418] border border-[#272A34] rounded-xl p-5 sm:p-6 mb-8" data-testid="post-detail">
          <div className="flex items-center gap-2 flex-wrap mb-3 text-[10px] font-mono uppercase tracking-wider">
            <span className="px-2 py-0.5 rounded-full border border-amber-800/40 bg-amber-950/30 text-amber-300">{t(`com_type_${post.type}`)}</span>
            {post.pinned && <span className="text-amber-400">{t("com_pinned")}</span>}
            {post.original_language && post.original_language !== "other" && <span className="text-stone-600">{post.original_language}</span>}
          </div>
          {title && <h1 className="font-serif text-2xl sm:text-3xl text-amber-100 mb-3">{title}</h1>}
          <div className="flex items-center gap-2 mb-4 text-sm">
            {post.author_picture ? <img src={post.author_picture} alt="" className="w-8 h-8 rounded-full object-cover" /> : <span className="w-8 h-8 rounded-full bg-amber-900/40 flex items-center justify-center"><User className="w-4 h-4 text-amber-400" /></span>}
            <span className="text-stone-300 flex items-center gap-1">{post.author_name}{official && !post.author_badges && <ShieldCheck className="w-4 h-4 text-amber-500" />}</span>
            <AuthorBadges badges={post.author_badges} />
            <span className="text-stone-600 font-mono text-xs">· {formatDate(post.created_at, lang)}</span>
          </div>
          {content && <TranslateText text={content} originalLanguage={post.original_language} className="mb-4" testId="translate-post" />}
          {post.event_slug && <Link to={`/events/${post.event_slug}`} className="inline-block text-xs text-amber-500 hover:text-amber-400 mb-3">→ {t("nav_events")}</Link>}
          <div className="flex items-center justify-between gap-3 flex-wrap pt-3 border-t border-[#272A34]">
            <ReactionBar targetType="post" targetId={post.id} counts={post.reaction_counts} mine={post.my_reactions} />
            <div className="flex items-center gap-3">
              <button data-testid="follow-btn" onClick={toggleFollow} className="inline-flex items-center gap-1.5 text-xs text-stone-400 hover:text-amber-400">{following ? <><BellOff className="w-3.5 h-3.5" /> {t("com_following")}</> : <><Bell className="w-3.5 h-3.5" /> {t("com_follow")}</>}</button>
              <button data-testid="share-btn" onClick={share} className="inline-flex items-center gap-1.5 text-xs text-stone-400 hover:text-amber-400"><Link2 className="w-3.5 h-3.5" /> {t("com_copy_link")}</button>
              {user && (post.author_id === user.user_id || user.role === "admin") && <button data-testid="del-post-btn" onClick={del} className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /> {t("com_delete")}</button>}
              {user && post.author_id !== user.user_id && <ReportDialog targetType="post" targetId={post.id} />}
              {user && post.author_id !== user.user_id && <BlockButton userId={post.author_id} />}
            </div>
          </div>
        </article>

        {/* Composer */}
        {user ? (
          <div className="mb-6 bg-[#121418] border border-[#272A34] rounded-lg p-4">
            {replyTo && <div className="text-xs text-amber-400 mb-2 flex items-center gap-2">{t("com_reply")} <button onClick={() => setReplyTo(null)} className="text-stone-500 hover:text-stone-300">✕</button></div>}
            <textarea data-testid="comment-input" rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("com_write_comment")} className="w-full bg-[#0B0C0E] border border-[#272A34] rounded-md px-3 py-2.5 text-sm text-stone-200 resize-none focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500" />
            <div className="flex justify-end mt-2"><button data-testid="comment-send" onClick={send} disabled={!body.trim()} className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-5 py-2 rounded-md text-sm disabled:opacity-50"><Send className="w-4 h-4" /> {t("com_send")}</button></div>
          </div>
        ) : <div className="text-center py-6 text-stone-400 border border-dashed border-[#272A34] rounded-lg mb-6">{t("com_login_to_post")}</div>}

        <div className="space-y-4" data-testid="comments-list">
          {topComments.length === 0 ? <p className="text-stone-500 text-sm text-center py-6">{t("com_event_first_message")}</p> :
            topComments.map((c) => (
              <div key={c.id} className="space-y-3">
                <Comment c={c} />
                {repliesOf(c.id).map((rc) => <Comment key={rc.id} c={rc} isReply />)}
              </div>
            ))}
        </div>
      </div>
    </>
  );
}
